import i18n, { createInstance, type i18n as I18n } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import en from './locales/en.json';
import ar from './locales/ar.json';
import ckb from './locales/ckb.json';
import { DEFAULT_LOCALE, LOCALES, LOCALE_STORAGE_KEY, type Locale } from './locales';

const RESOURCES = { en: { translation: en }, ar: { translation: ar }, ckb: { translation: ckb } };

/**
 * The product is Arabic first (owner's call, 2026-09-17): a first visit with
 * nothing stored and no signed-in preference renders Arabic, RTL. The
 * browser's own language is deliberately not consulted — only a choice the
 * user made here (localStorage) or their `preferredLocale` from the backend,
 * which the auth store applies when a session is established.
 */
export function configureI18n(instance: I18n = createInstance()): I18n {
  void instance
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: RESOURCES,
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: [...LOCALES],
      nonExplicitSupportedLngs: true,
      interpolation: { escapeValue: false },
      detection: { order: ['localStorage'], caches: ['localStorage'], lookupLocalStorage: LOCALE_STORAGE_KEY },
    });
  return instance;
}

configureI18n(i18n);

const fixedInstances = new Map<Locale, I18n>();

/**
 * A second instance pinned to one language, for a document printed in a
 * language other than the console's (the prescription sheet's English | عربي
 * switch). Hand it to an `I18nextProvider` around the document and everything
 * inside that calls `useTranslation` reads it.
 *
 * Deliberately **not** `i18n.cloneInstance()` + `changeLanguage()`: the clone
 * shares the language detector, which writes every change to localStorage, so
 * printing one sheet in English would switch the whole console to English on
 * the next load. This one has no detector and no react binding of its own.
 */
export function fixedLanguageI18n(locale: Locale): I18n {
  let instance = fixedInstances.get(locale);
  if (!instance) {
    instance = createInstance();
    void instance.init({
      resources: RESOURCES,
      lng: locale,
      fallbackLng: DEFAULT_LOCALE,
      interpolation: { escapeValue: false },
      initAsync: false,
    });
    fixedInstances.set(locale, instance);
  }
  return instance;
}

export default i18n;
